import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Hub from "../../../api/Hub";
import Container from "../../../components/base/Container";
import Div from "../../../components/base/Div";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import Indicator from "../../../components/base/Indicator";
import Modal from "../../../components/base/Modal";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import AvatarUploade from "../../../components/form/AvatarUploade";
import Form from "../../../components/form/Form";
import TextBox from "../../../components/form/TextBox";
import { getUSA, setOwnerUserId, setSignupStep, setUserId, signature, useAllCountries, useEnvironment, useHub, useOwnerUserId, usePrices } from "../../../core/Global";
import HUBResponse from "../../../models/HUBResponse";
import PaymentMethod from "../../system/settings/models/PaymentMethod";
import SignupRequest from "../models/SignupRequest";
import DataItem from "../../../models/DataItem";
import Select from "../../../components/form/Select";
import PhoneInput from "../../../components/form/PhoneInput";
import Country from "../../base/models/Country";
import TwilioLine from "../../voice/lines/models/TwilioLine";
import { getPasswordStrength, PasswordStrengthResult } from "../../../core/Helper";
import PlanCatalog, { PlanBilling } from "../../../components/plans/PlanCatalog";
import SignupJobTypes from "../components/SignupJobTypes";
import SignupJobSources from "../components/SignupJobSources";
import SignupNumberSettings from "../components/SignupNumberSettings";
import SignupWallet from "../components/SignupWallet";
import SignupActions from "../components/SignupActions";
import "../components/SignupWelcome.css";
import { Plan } from "../../../components/plans/plans";
import NumberResults from "../../../components/lines/NumberResults";
import EnterpriseOfferStep from "../components/EnterpriseOfferStep";
import { OfferView } from "../../enterprise/models/Enterprise";


// US area codes for the number search, grouped by the state they belong to.
//
// Declared outside the component so it is not a derived const sitting between hooks, which this
// project's ESLint reads as a conditional hook call.
const usAreaCodeItems = (states?: any[]) =>
    (states ?? []).flatMap((st: any) =>
        (st.areaCodes ?? []).map((code: any) =>
            ({ id: String(code), name: String(code), group: st.name })))


// A country, with its flag, in the select and in the closed box.
//
// The flags come as squares, and the project rounds them wherever one is shown beside a name --
// the phone-code picker and .img-drp-flag both do the same. Declared outside the component so it
// is the same function on every render rather than a new one each time.
const countryOption = (item: Country) => (
    <span className="d-flex align-items-center gap-2">
        {
            item.flagSquare &&
            <img src={item.flagSquare} alt="" className="signup-country-flag" />
        }
        <span>{item.name}</span>
    </span>
)


function SignUpComplete() {

    const { token } = useParams();
    const hub = useHub()
    const env = useEnvironment()
    const ownerUserId = useOwnerUserId()
    const countries = useAllCountries();

    const prices = usePrices();

    const moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    // Plan prices are whole dollars and are written that way on the cards, so the button that
    // charges one says $349 rather than $349.00.
    const planMoneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    });

    const [req, reqSet] = useState<SignupRequest>();
    const [step, stepSet] = useState<number>(0);
    const [working, workingSet] = useState<boolean>(false);
    const [loading, loadingSet] = useState<boolean>(true);

    const [usernameVerifing, usernameVerifingSet] = useState<boolean>(false);
    const [usernameVerified, usernameVerifiedSet] = useState<boolean>(false);

    const [passwordStrength, passwordStrengthSet] = useState<PasswordStrengthResult>(getPasswordStrength());


    const [states, statesSet] = useState<any[]>([]);
    const [companyCountryId, companyCountryIdSet] = useState<string>();
    const [companyPhone, companyPhoneSet] = useState<string>();

    const [paymentMethods, paymentMethodsSet] = useState<PaymentMethod[]>([]);
    const [selectedItem, selectedItemSet] = useState<PaymentMethod>();
    const [fetchingPaymentMethods, fetchingPaymentMethodsSet] = useState<boolean>(true);
    // Free is preselected: it is the one plan nobody has to be talked into, and it keeps the
    // step from starting on nothing.
    const [selectedPlan, selectedPlanSet] = useState<string>("free");
    const [selectedPlanItem, selectedPlanItemSet] = useState<Plan>();
    const [planBilling, planBillingSet] = useState<PlanBilling>("monthly");
    const [planWorking, planWorkingSet] = useState<boolean>(false);
    // A sign-up started from an Enterprise offer pays for that offer instead of choosing a plan.
    const [quote, quoteSet] = useState<OfferView>();

    const [monthlyBudget, monthlyBudgetSet] = useState<number>(25);
    const [monthlyBudgetVerified, monthlyBudgetVerifiedSet] = useState<boolean>(false);

    const [subTotalAmount, subTotalAmountSet] = useState<number>(0);
    const [totalAmount, totalAmountSet] = useState<number>(0);


    const [selectedCountry, selectedCountrySet] = useState<Country>();

    const fetch = () => {
        hub.Get("/auth/signup/request/" + token, res => {
            if (res.success && res.data) {
                reqSet(res.data);
                loadingSet(false);
                stepSet(res.data.step);
            }
            else {
                //console.log("errrrrrrrrrrrrrrrrrr", res);
                document.location.href = "/auth/login";
            }
        });
    }

    const usernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        usernameVerifiedSet(false);
        usernameVerifingSet(false);
        window.hideFormErrors();
        if (e.target.value.length < 4) return;
        usernameVerifingSet(true);
        hub.Post("/auth/signup/username", {
            username: e.target.value
        }, res => {
            usernameVerifingSet(false);
            if (res.success) {
                usernameVerifiedSet(true);
            }
            else {
                window.showFormError(res.field, res.message, true);
            }
        });

    }

    const submitSignInOptions = (body: any, callback?: (res?: HUBResponse<any>) => void) => {

        window.hideFormErrors();
        usernameVerifingSet(false);
        usernameVerifiedSet(false);

        if (window.isNullOrEmpty(body.username)) {
            window.showFormError("username", "username is required!");
            return;
        }

        if (window.isNullOrEmpty(body.password)) {
            window.showFormError("password", "password is required!");
            return;
        }

        // One filled bar is refused as too weak — the meter and this check share getPasswordStrength().
        if (!getPasswordStrength(body.password).acceptable) {
            window.showFormError("password", "password is too weak! use 8 or more characters with a mix of letters, numbers and symbols.");
            return;
        }

        workingSet(true);

        hub.Post("/auth/signup/signinoptions", {
            ...body,
            token: req?.token
        }, res => {
            if (res.success) {
                stepSet(3);
                reqSet(res.data);
            }
            else {
                window.showFormError(res.field, res.message);
            }

            workingSet(false);
        });

    }


    const fetchStates = (countryId: string) => {
        hub.Get("/base/states/?countryId=" + countryId, res => {
            if (res.data) statesSet(res.data);
        });
    }

    useEffect(() => {
        if (selectedCountry?.id) {
            fetchStates(selectedCountry.id);
        }
    }, [selectedCountry])

    const submitCompany = (body: any, callback?: (res?: HUBResponse<any>) => void) => {
        window.hideFormErrors();
        var valid = true;
        if (window.isNullOrEmpty(body.companyName) || body.companyName.trim().length < 2) {
            window.showFormError("companyName", "Please write your business name!");
            valid = false;
        }

        // Checked in the order the fields appear, so the first error is the one that takes focus.
        if (window.isNullOrEmpty(body.companyCountry)) {
            window.showFormError("companyCountry", "Please choose the country!");
            valid = false;
        }

        if (window.isNullOrEmpty(body.companyState)) {
            window.showFormError("companyState", "Please choose the state!");
            valid = false;
        }

        if (window.isNullOrEmpty(body.companyCity) || body.companyCity.trim().length < 2) {
            window.showFormError("companyCity", "Please write a valid city name!");
            valid = false;
        }

        if (window.isNullOrEmpty(body.companyAddressLine1) || body.companyAddressLine1.trim().length < 5) {
            window.showFormError("companyAddressLine1", "Please write a valid address!");
            valid = false;
        }

        if (!window.isNullOrEmpty(body.companyPhone) && body.companyPhone.length < 9) {
            window.formError("companyPhone", "Phone number is not valid!");
            valid = false;
        }

        if (!valid) return;

        workingSet(true);
        hub.Post("/auth/signup/company", {
            ...body,
            token: req?.token
        }, res => {
            if (res.success) {
                stepSet(4);
                reqSet(res.data);
            }
            else {
                if (res.field) window.showFormError(res.field, res.message);
                else toastError(res.message ?? "general error");
            }
            workingSet(false);
        })

    }

    const updateStep = (st: number) => {
        stepSet(st);
        hub.Post("/auth/signup/updatestep", {
            token: req?.token,
            step: st
        }, res => { });
    }

    // Free costs nothing, so the backend finishes the account there and then. A paid plan comes
    // back with a Stripe Checkout to follow, which returns to the last step once it is settled.
    const submitPlan = () => {
        planWorkingSet(true);
        hub.Post(req?.quoteId ? "/auth/signup/selectquote" : "/auth/signup/selectplan", {
            token: req?.token,
            planCode: selectedPlan,
            billing: planBilling
        }, res => {
            if (!res.success) {
                planWorkingSet(false);
                toastError(res.message ?? "general error");
                return;
            }

            if (res.data?.paid && res.data?.url) {
                document.location.href = res.data.url;
                return;
            }

            planWorkingSet(false);
            fetch();
            stepSet(6);
        });
    }


    const budgetKeypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/[0-9]/.test(e.key) && e.key != "Enter") {
            e.preventDefault();
        }
    }

    const budgetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        window.hideFormErrors();
        monthlyBudgetVerifiedSet(false);
        var budg = Number.parseInt(e.target.value);
        if (isNaN(budg)) budg = 0;
        monthlyBudgetSet(budg);
        if (budg >= 25 && budg <= 300) {
            monthlyBudgetVerifiedSet(true);
        }
        else {
            window.showFormError("monthlyBudget", "Monthly budget must be between 50$ to 300$", true);
        }
    }

    const fetchPaymentMethods = (callback?: any) => {
        fetchingPaymentMethodsSet(true);
        hub.Post("/auth/signup/paymentmethods", {
            userId: req?.userId,
            token: req?.token
        }, res => {
            if (res.success) {
                paymentMethodsSet(res.data);
                if (callback) callback();
                fetchingPaymentMethodsSet(false);
            }
        });

    }

    const PaymentCardView = (props: { item: PaymentMethod }) => (
        <div>
            <FlexRow>
                {
                    props.item.cardBrand == "visa" ?
                        <img src="/assets/media/svg/card-logos/visa.svg" alt="" className="me-4 h-50px" />
                        :
                        props.item.cardBrand == "mastercard" ?
                            <img src="/assets/media/svg/card-logos/mastercard.svg" alt="" className="me-4 h-50px" />
                            :
                            props.item.cardBrand == "amex" ?
                                <img src="/assets/media/svg/card-logos/american-express-dark.svg" alt="" className="me-4 h-50px" />
                                :
                                props.item.cardBrand == "discover" ?
                                    <img src="/assets/media/svg/card-logos/discover.png" alt="" className="me-4 h-50px" />
                                    :
                                    props.item.cardBrand == "diners" ?
                                        <img src="/assets/media/svg/card-logos/diners-club.png" alt="" className="me-4 h-50px" />
                                        :
                                        props.item.cardBrand == "jcb" ?
                                            <img src="/assets/media/svg/card-logos/jcb.png" alt="" className="me-4 h-50px" />
                                            :
                                            props.item.cardBrand == "unionpay" ?
                                                <img src="/assets/media/svg/card-logos/unionpay.svg" alt="" className="me-4 h-50px" />
                                                :
                                                <img src="/assets/media/svg/card-logos/unknown.png" alt="" className="me-4 h-50px" />
                }
                <div>
                    <div className="fs-4 fw-bolder">{props.item.cardBrand}&nbsp;****&nbsp;{props.item.cardLast4}</div>
                    <div className="fs-6 fw-bold text-gray-400">Exp: **/{props.item.cardExpYear}</div>
                </div>
            </FlexRow>
        </div>
    )

    const paymentmethodSubmit = (body: any, callback?: (res?: HUBResponse<any>) => void) => {
        window.hideFormErrors();

        workingSet(true);

        hub.Post("/auth/signup/paymentmethod", {
            ...body,
            userId: req?.userId,
            token: req?.token
        }, res => {
            if (res.success) {
                setTimeout(() => {
                    document.location.href = res.data.url;
                }, 1000);
            }
            else {
                if (res.field) window.showFormError(res.field, res.message);
                else toastError(res.message ?? "general error happened!");
                workingSet(false);
            }
        });

    }

    const budgetSubmit = (body: any, callback?: (res?: HUBResponse<any>) => void) => {
        window.hideFormErrors();
        return;
        if (window.isNullOrEmpty(body.monthlyBudget)) {
            window.showFormError("monthlyBudget", "Please define your monthly budget!");
            return;
        }

        var budget = Number.parseInt(body.monthlyBudget);
        if (isNaN(budget) || budget < 50 || budget > 300) {
            window.showFormError("monthlyBudget", "Monthly budget must be between 50$ to 300$");
            return;
        }

        workingSet(true);

        hub.Post("/auth/signup/savebudget", {
            ...body,
            userId: req?.userId,
            token: req?.token
        }, res => {
            if (res.success) {
                reqSet(res.data);
                stepSet(6);
            }
            else {
                if (res.field) window.showFormError(res.field, res.message);
                else toastError(res.message ?? "general error happened!");
            }
            workingSet(false);
        });

    }

    const payInvoice = () => {
        window.hideFormErrors();

        if (monthlyBudget < 25 || monthlyBudget > 300) {
            window.showFormError("invoice-budget", "Budget limit must be between 25$ to 300$");
            return;
        }
        workingSet(true);

        setTimeout(() => {

            hub.Post("/auth/signup/payinvoice", {
                token: req?.token,
                userId: req?.userId,
                monthlyBudget: monthlyBudget,
                couponCode: couponCode
            }, res => {
                if (res.success) {
                    document.location.href = res.data.checkPaymentLink;
                }
                else {
                    window.showFormError("invoice", res.message);
                    workingSet(false);
                }
            });

        }, 200);


    }

    const beginsetup = (token?: string, userId?: string) => {
        workingSet(true);
        hub.Post("/auth/signup/beginsetup", {
            token: token,
            userId: userId
        }, res => {
            if (res.success) {

                signature(res.data.session.signature);
                setUserId(res.data.authUser.id);

                // Read off authUser, which is where they are. These used to be read off res.data,
                // where none of them existed, so the owner was never set and the rest of the
                // setup wizard fell back on whatever the browser still had stored — another
                // account's id, if one had signed in here before.
                if (res.data.authUser.isSuperAdmin) setOwnerUserId("admins");
                else if (res.data.authUser.isCustomer) setOwnerUserId(res.data.authUser.id);
                else if (res.data.authUser.ownerUserId) setOwnerUserId(res.data.authUser.ownerUserId);

                document.location.reload();
            }
            else {
                if (res.field) window.showFormError(res.field, res.message);
                else toastError(res.message ?? "general error");
            }
            workingSet(false);
        });
    }

    // What this number will cost, asked of the plan the account is actually on. Free includes
    // none, so on Free it comes back as a monthly charge against the wallet rather than "Free".
    const [numberQuote, numberQuoteSet] = useState<{ isFree: boolean, priceText?: string, note?: string }>();

    const [fetchingLines, fetchingLinesSet] = useState<boolean>();
    const [fetchedLines, fetchedLinesSet] = useState<boolean>();
    const [lines, linesSet] = useState<TwilioLine[]>();
    const [selectedLine, selectedLineSet] = useState<TwilioLine>();
    let b: any;
    const fetchLines = (params?: any) => {

        fetchedLinesSet(false);
        fetchingLinesSet(true);
        selectedLineSet(undefined);
        params = params ?? b;
        b = params;

        var q = "";
        if (params?.country && params.country != "") {
            if (q != "") q += "&";
            q += "country=" + params.country;
        }
        if (params?.areaCode && params.areaCode != "") {
            if (q != "") q += "&";
            q += "areaCode=" + params.areaCode;
        }
        if (params?.keyword && params.keyword != "") {
            if (q != "") q += "&";
            q += "keyword=" + encodeURIComponent(params.keyword);
        }
        if (params?.match) {
            if (q != "") q += "&";
            q += "match=" + encodeURIComponent(params.match);
        }

        if (params?.voice == "True") {
            if (q != "") q += "&";
            q += "voice=True";
        }

        if (params?.sms == "True") {
            if (q != "") q += "&";
            q += "sms=True";
        }

        if (params?.mms == "True") {
            if (q != "") q += "&";
            q += "mms=True";
        }

        if (selectedNumberType) {
            if (q != "") q += "&";
            q += "numberType=" + selectedNumberType.id;
        }

        // A longer list than the console's: this is the one place a customer picks their number
        // with nothing else to go on.
        if (q != "") q += "&";
        q += "limit=50";

        if (q != "") q = "?" + q;

        hub.Get("/voice/lines/available/" + q, res => {
            if (res.data) {
                linesSet(res.data);
                fetchedLinesSet(true);
                fetchingLinesSet(false);
            }
        })

    }

    // Choosing a number only reserves the choice. Nothing is bought until the next step, where
    // its calls are given somewhere to go and the whole setup is finished in one go.
    const chooseNumber = () => {
        window.hideFormError();

        if (!selectedLine) {
            toastError("Choose a phone number to continue.");
            return;
        }

        stepSet(11);
    }

    // Leaves the current setup step undone and moves to the next one.
    const skipStep = () => updateStep(step + 1);

    // Leaves the whole setup undone and opens the console. Setting the account up is the same
    // work whether it is done here or from inside the product, so nobody has to do it now.
    const skipSetup = () => {
        workingSet(true);
        finalize();
    }

    const finalize = () => {
        hub.Post("/auth/signup/finalize", {
            token: req?.token,
            userId: req?.userId
        }, res => {
            if (res.success) {
                signature(res.data.session.signature);
                setUserId(res.data.authUser.id);

                // The owner too, exactly as beginsetup does it. Left out here, the browser kept
                // whatever owner it already had -- the previous account's, if anybody had signed
                // in on it before -- and carried it into the console the wizard hands over to.
                if (res.data.authUser.isSuperAdmin) setOwnerUserId("admins");
                else if (res.data.authUser.isCustomer) setOwnerUserId(res.data.authUser.id);
                else if (res.data.authUser.ownerUserId) setOwnerUserId(res.data.authUser.ownerUserId);

                // Where signing in lands too, and for the same reason: the job board is the
                // product for a Rinvox account, and the dashboard is a reporting screen with
                // nothing on it yet for somebody who has just registered. Both ways out of the
                // wizard come through here -- finishing it and skipping it -- so they cannot
                // disagree about where the account opens.
                document.location.href = env.brand == "rinvox" ? "/boards/jobs" : "/dashboard";
            }
            else {
                if (res.field) window.showFormError(res.field, res.message);
                else toastError(res.message ?? "general error happened!");
                workingSet(false);
            }
        });
    }

    useEffect(fetch, []);

    useEffect(() => {
        if (!req?.quoteId || !req?.token) return;
        hub.Get("/auth/signup/quote/" + req.token, res => {
            if (res.success) quoteSet(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [req?.quoteId]);

    useEffect(() => {
        setSignupStep(step);
        if (step == 2) {
            $("#txt-username").focus();
            window.KTPasswordMeterCreate();
        }
        if (step == 3) {
            if (req?.companyCountry) fetchStates(req.companyCountry);
        }
        if (step == 4) {
            fetchPaymentMethods();
        }
        if (step == 6 && req?.planCode && selectedPlanItem?.code != req.planCode) {
            // Coming back from Stripe reloads the page, so the chosen plan has to be read again
            // to be able to name it on the welcome screen.
            hub.Get("/base/public/plans", res => {
                if (res.success && res.data) selectedPlanItemSet(res.data.find((p: Plan) => p.code == req.planCode));
            });
        }
        if (step == 10) {
            fetchLines();
            selectedCountrySet(getUSA());
            hub.Get("/voice/lines/numberquote/?numberType=" + (selectedNumberType?.id ?? 1), res => {
                if (res.success) numberQuoteSet(res.data);
            });
        }
        if (step == 11 && !selectedLine) {
            stepSet(10);
        }
    }, [step, req])

    // How the wallet step went, told once on the way back from the payment page.
    //
    // The step itself is gone by then: paying leaves Stripe, lands on the hub and comes back to
    // a freshly loaded wizard, so the only thing that survives the round trip is what the hub put
    // in the address bar. It is taken straight back out of it, or a reload would say it again.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const wallet = params.get("wallet");
        if (!wallet) return;

        if (wallet == "success") toastSuccess("Your wallet has been topped up.");
        else if (wallet == "failed") toastError("The payment did not go through. Please try again.");

        params.delete("wallet");
        const query = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (query ? "?" + query : ""));
    }, []);

    // The area-code filter is built from the US states. They used to be loaded only on the
    // company step, so arriving here any other way -- a reload, a company outside the US -- left
    // the list empty. Asked again once the countries are known.
    useEffect(() => {
        if (step != 10) return;
        const us = getUSA();
        if (us?.id) fetchStates(us.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, countries])

    useEffect(() => {
        $("#drp-companyState").val(req?.companyState ?? "");
        window.updateSelect2("drp-companyState", req?.companyState);
        if (req?.token) localStorage.setItem("signup_req_token", req.token);
    }, [states, req]);


    const [couponCode, couponCodeSet] = useState<string>("START149");
    const [couponAmount, couponAmountSet] = useState<number>(0);
    const [couponChecking, couponCheckingSet] = useState<boolean>(false);
    const [couponIsValid, couponIsValidSet] = useState<boolean>(false);
    const [couponIsInvalid, couponIsInvalidSet] = useState<boolean>(false);

    useEffect(() => {
        couponIsValidSet(false);
        couponIsInvalidSet(false);
        couponAmountSet(0);
        if (couponCode && couponCode.length == 8) {
            couponCheckingSet(true);
            setTimeout(() => {
                if (couponCode.toLowerCase() == "start149") {
                    couponIsValidSet(true);
                    couponAmountSet(149);
                }
                else {
                    couponIsInvalidSet(true);
                }
                couponCheckingSet(false);
            }, 500);
        }
    }, [couponCode])

    useEffect(() => {
        var total = (prices?.subscriptionPerMonth ?? 0) + monthlyBudget;
        subTotalAmountSet(total);
        total = total - couponAmount;
        if (total < 0) total = 0;
        totalAmountSet(total);
    }, [monthlyBudget, couponAmount, prices])


    // Sign-up offers local numbers only; toll-free can be added from the console.
    const [selectedNumberType] = useState<DataItem>({
        id: "1",
        name: "Local"
    });


    // What the chosen plan costs for the period being bought — 0 on Free, which is what tells
    // the step there is nothing to pay and no card to collect.
    const planPrice = selectedPlanItem
        ? (planBilling == "annual" && selectedPlanItem.annualPrice > 0 ? selectedPlanItem.annualPrice : selectedPlanItem.monthlyPrice)
        : 0;

    // After a Stripe round-trip the page comes back fresh, so the last step reads what was
    // chosen from the request rather than from state that no longer exists.
    const finishedOnFreePlan = !req?.quoteId && (req?.planCode ?? "free") == "free";

    // Named on the welcome screen when it is known. Left out rather than guessed at: the plan
    // is looked up from the catalog, and the page reads fine without it.
    const planLabel = req?.quoteId ? "Enterprise" : selectedPlanItem?.code == req?.planCode ? selectedPlanItem?.name : undefined;

    // How wide this step is. The action bar is fixed to the bottom of the window, so it is told
    // the same width and centres itself on it — otherwise its buttons would sit against the edge
    // of the screen instead of lining up with the form above them.
    // The account-setup steps (7 onwards) are given more room than the sign-up ones: they are
    // lists and builders rather than a handful of fields.
    const stepWidth = step == 4 ? 1240 : step == 10 ? 1400 : step >= 7 ? 900 : step == 5 ? 800 : 600;

    // Nothing in the setup wizard has to be done now — all of it can be done from the console —
    // so every one of those steps offers a way past it and a way out of the whole thing.
    const inSetupWizard = step >= 7 && step <= 11;

    // No top padding on the step itself: the column it sits in already starts it on the same
    // line the wizard beside it starts on.
    return (
        <div className="w-100 px-10 mx-auto"
            style={{
                maxWidth: stepWidth,
                // Room for the fixed action bar, so the last of the content is never behind it.
                // The bar measures 65px; the rest is the gap above it. It used to be 120, which
                // is 55px of nothing added to the height of every step.
                paddingBottom: 90
            }}>

            <Container visible={loading}>
                <Indicator text="Please wait..." />
            </Container>

            <Container visible={step == 1}>
                <form className="form w-100" id="kt_sign_up_form">
                    <div className="mb-10 text-center">
                        <div className="text-center mb-10">
                            <Icon type="thin" icon="check-circle" size={96} className="me-2 text-success" />
                        </div>
                        <h1 className="fw-bolder fs-2qx text-gray-800 mb-7">
                            Thank You {req?.firstName}!
                        </h1>
                        <div className="text-gray-600 fs-6">
                            Your email address <span className="text-success fw-bolder">{req?.email}</span> has been verified successfully!
                        </div>
                        <div className="text-gray-600 fs-6">
                            You can complete your registration process now!
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>
                    <SignupActions>
                        <button type="button" className="btn btn-lg btn-primary ms-auto" onClick={e => stepSet(2)}>
                            <span className="indicator-label">
                                Let's Keep Going
                            </span>
                        </button>
                    </SignupActions>
                </form>
            </Container>

            <Container visible={step == 2}>
                <Form onSubmit={submitSignInOptions} className="form w-100">
                    <div className="mb-10 text-center">
                        <h1 className="text-dark mb-3">Sign-In Options</h1>
                        <div className="text-gray-400 fw-bold fs-6">
                            Create your own username and password!
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>

                    <div className="fv-row mb-7">
                        <label className="form-label fs-7" htmlFor="txt-username">
                            Username
                            <span id="username-err" className="small text-danger mx-2 form-error"></span>
                        </label>
                        <div className="position-relative mb-3">
                            <input id="txt-username" className="form-control form-inputs" onChange={usernameChange}
                                type="text" placeholder="Choose your username" name="username" autoComplete="one-time-code" />
                            <span className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2" data-kt-password-meter-control="visibility">
                                {
                                    usernameVerifing && <i className="far fa-spinner fa-spin fs-2"></i>
                                }
                                {
                                    usernameVerified && <i className="far fa-check-circle fs-2 text-success"></i>
                                }
                            </span>
                        </div>
                    </div>

                    <div className="mb-10 fv-row" data-kt-password-meter="true">
                        <div className="mb-1">
                            <label className="form-label fs-7" htmlFor="txt-password">
                                Password
                                <span id="password-capslock" className="small text-danger mx-2 form-error">CapsLock is On</span>
                                <span id="password-err" className="small text-danger mx-2 form-error"></span>
                            </label>
                            <div className="position-relative mb-3">
                                <input id="txt-password" className="form-control form-inputs" type="password" placeholder="Choose a strong password" name="password" autoComplete="one-time-code"
                                    onChange={e => {
                                        // Typing clears the error; if it is still weak it comes back on the next submit.
                                        window.hideFormError("password");
                                        passwordStrengthSet(getPasswordStrength(e.target.value));
                                    }}
                                    onKeyUp={e => {
                                        if (e.getModifierState("CapsLock")) {
                                            $("#password-capslock").fadeIn().css("display", "inline-block");
                                        }
                                        else {
                                            $("#password-capslock").hide();
                                        }
                                    }} />
                                <span className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2" data-kt-password-meter-control="visibility">
                                    <i className="bi bi-eye-slash fs-2"></i>
                                    <i className="bi bi-eye fs-2 d-none"></i>
                                </span>
                            </div>
                            {/* The bars keep the meter attribute so the eye toggle still works, but their
                                colour comes from getPasswordStrength, not from the plugin's active class. */}
                            <div className="d-flex align-items-center mb-3" data-kt-password-meter-control="highlight">
                                {
                                    [1, 2, 3, 4].map(bar =>
                                        <div key={bar}
                                            className={"flex-grow-1 rounded h-5px "
                                                + (bar < 4 ? "me-2 " : "")
                                                + (bar <= passwordStrength.bars ? "bg-" + passwordStrength.color : "bg-secondary")}>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        <div className={passwordStrength.color ? "text-" + passwordStrength.color : "text-muted"}>
                            {passwordStrength.message}
                        </div>
                    </div>

                    <SignupActions>
                        <button type="submit" className="btn btn-primary min-w-150px ms-auto" disabled={working}>
                            {
                                working ?
                                    <>
                                        <span className="indicator-label me-2">
                                            Please Wait
                                        </span>
                                        <Icon icon="spinner-third" spin />
                                    </>
                                    :
                                    <>
                                        <span className="indicator-label me-2">
                                            Continue
                                        </span>
                                        <Icon icon="chevron-right" type="solid" />
                                    </>
                            }
                        </button>
                    </SignupActions>

                </Form>
            </Container>

            <Container visible={step == 3}>
                <Form className="form w-100" onSubmit={submitCompany}>
                    <div className="mb-10 text-center">
                        <h1 className="text-dark mb-3">Add Business</h1>
                        <div className="text-gray-400 fw-bold fs-6">
                            Add your company or your personal business information!
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>

                    <div className="text-center mb-7">
                        <AvatarUploade name="logoUrl" type="logos" width={80} />
                    </div>
                    <div className="fv-row mb-3">
                        <TextBox name="companyName" placeholder="Write your company name" label="Business Name" labelUp
                            autoFocus block defaultValue={req?.companyName} />
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Select id="drp-join-countries" name="companyCountry" block labelUp marginBottom={0}
                                label="Country" source={countries} defaultValue={req?.companyCountry}
                                optionTemplate={countryOption}
                                selectionTemplate={countryOption}
                                onSelect={val => {
                                    companyCountryIdSet(val);
                                    fetchStates(val ?? "");
                                }} />
                        </div>
                        <div className="col-md-6">
                            {
                                states.length > 0 ?
                                    // Keyed on the country so picking a different one starts the state
                                    // over: the box already cleared itself, but the value it posted did not.
                                    <Select key={"state-" + companyCountryId} id="drp-join-states"
                                        name="companyState" block labelUp marginBottom={0}
                                        label="State" source={states} defaultValue={req?.companyState} />
                                    :
                                    <TextBox name="companyState" block labelUp marginBottom={0}
                                        label="State" defaultValue={req?.companyStateCustom} />
                            }

                        </div>
                    </div>

                    {/* <div className="fv-row mb-3">
                        <DropDown id="drp-join-countries" name="companyCountry" block labelUp marginBottom={0}
                            label="Country" source={countries} defaultValue={req?.companyCountry}
                            onSelect={val => fetchStates(val ?? "")}/>
                    </div>

                    <div className="fv-row mb-3">
                        
                    </div> */}

                    <div className="fv-row mb-3">
                        <TextBox block labelUp marginBottom={0} name="companyCity" value={req?.companyCity}
                            label="City" placeholder="What city is your business in?" />
                    </div>
                    <div className="fv-row mb-3">
                        <TextBox block labelUp marginBottom={0} name="companyAddressLine1" value={req?.companyAddressLine1}
                            label="Address Line 1" placeholder="District, Street name" />
                    </div>
                    <div className="fv-row mb-3">
                        <TextBox block labelUp marginBottom={0} name="companyAddressLine2" value={req?.companyAddressLine2}
                            label="Address Line 2" placeholder="Building Number, Entrance, Apartment" />

                    </div>
                    <div className="fv-row mb-10">
                        <PhoneInput name="companyPhone" labelUp block label="Company Phone" marginBottom={0}
                            defaultPhone={req?.companyPhoneItem} countryCodes="all" />
                    </div>
                    <SignupActions>
                        <button type="submit" className="btn btn-primary min-w-150px ms-auto" disabled={working}>
                            {
                                working ?
                                    <>
                                        <span className="indicator-label me-2">
                                            Please Wait
                                        </span>
                                        <Icon icon="spinner-third" spin />
                                    </>
                                    :
                                    <>
                                        <span className="indicator-label me-2">
                                            Continue
                                        </span>
                                        <Icon icon="chevron-right" type="solid" />
                                    </>
                            }
                        </button>
                    </SignupActions>
                </Form>
            </Container>

            <Container visible={step == 4}>
                <div className="w-100">
                    <div className="mb-8 text-center">
                        <h1 className="text-dark mb-3">{req?.quoteId ? "Your Enterprise Plan" : "Choose Your Plan"}</h1>
                        <div className="text-gray-400 fw-bold fs-6">
                            {
                                req?.quoteId
                                    ? "The offer you signed. Paying for it builds your account on it."
                                    : "Pick the plan that fits your operation. You can change it later."
                            }
                        </div>
                    </div>

                    {
                        req?.quoteId && <EnterpriseOfferStep offer={quote} />
                    }

                    {/* No Enterprise here: it has no price until a representative quotes one,
                        so it is not something a signup can end on. */}
                    {
                        !req?.quoteId &&
                    <PlanCatalog showEnterprise={false}
                        value={selectedPlan}
                        billing={planBilling}
                        onBillingChange={b => planBillingSet(b)}
                        onSelect={(code, plan) => {
                            selectedPlanSet(code);
                            selectedPlanItemSet(plan);
                        }}
                        onLoaded={plans => selectedPlanItemSet(plans.find(p => p.code == selectedPlan))} />
                    }

                    <SignupActions>
                        <button type="button" className="btn btn-light" onClick={e => updateStep(3)} disabled={planWorking}>
                            <Icon icon="chevron-left" className="me-2" />
                            Edit Business
                        </button>

                        {/* The button says what is about to happen: a paid plan is charged on the
                            next screen, so it names the amount rather than saying "Continue".
                            No btn-icon here — that class is for icon-only buttons and strips the
                            padding, which left the label pressed against the edges. */}
                        <button type="button" className="btn btn-primary min-w-200px ms-auto"
                            disabled={(req?.quoteId ? !quote : !selectedPlan) || planWorking} onClick={submitPlan}>
                            {
                                planWorking ?
                                    <>
                                        <span className="indicator-label me-2">Please Wait</span>
                                        <Icon icon="spinner-third" spin />
                                    </>
                                    : req?.quoteId ?
                                        <>
                                            <Icon icon="lock" type="solid" size={14} className="me-2" />
                                            <span className="indicator-label">
                                                Pay {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(quote?.chargeToday ?? 0)} &amp; Continue
                                            </span>
                                        </>
                                    : planPrice > 0 ?
                                        <>
                                            <Icon icon="lock" type="solid" size={14} className="me-2" />
                                            <span className="indicator-label">
                                                Pay {planMoneyFormatter.format(planPrice)} &amp; Continue
                                            </span>
                                        </>
                                        :
                                        <>
                                            <span className="indicator-label me-2">Continue</span>
                                            <Icon icon="chevron-right" />
                                        </>
                            }
                        </button>
                    </SignupActions>
                </div>
            </Container>

            <Container visible={step == 5}>
                <form onSubmit={e => e.preventDefault()} className="form w-100">
                    <div className="mb-10 text-center">
                        <h1 className="text-dark mb-3">
                            First Invoice
                        </h1>
                        <div className="text-gray-600 fw-bold fs-6">
                            Customize and pay your first invoice
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>
                    <div className="flex-lg-row-fluid me-xl-18 mb-10 mb-xl-0">
                        <div className="mt-n1">
                            {/* <div className="d-flex flex-stack pb-10">
                                <a href="#">
                                    <img alt="Logo" src="/assets/media/svg/brand-logos/code-lab.svg" />
                                </a>
                                <a href="#" className="btn btn-sm btn-success">Pay Now</a>
                            </div> */}

                            <div className="m-0">
                                {/* <div className="fw-bolder fs-3 text-gray-800 mb-8">Invoice #34782</div> */}
                                <div className="row g-5 mb-11">
                                    <div className="col-sm-3">
                                        <div className="fw-bold fs-7 text-gray-600 mb-1">Issue Date:</div>
                                        <div className="fw-bolder fs-6 text-gray-800">
                                            {window.now().format("DD MMM YYYY")}
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="fw-bold fs-7 text-gray-600 mb-1">Issue For:</div>
                                        <div className="fw-bolder fs-6 text-gray-800">
                                            {req?.companyName}
                                        </div>
                                    </div>
                                    <div className="col-sm-6 text-end">

                                    </div>
                                </div>
                            </div>

                            {/* <div className="row g-5 mb-12">
                                <div className="col-sm-9">
                                    <div className="fw-bold fs-7 text-gray-600 mb-1">Issue For:</div>
                                    <div className="fw-bolder fs-6 text-gray-800">
                                        {req?.companyName}
                                    </div>
                                    <div className="fw-bold fs-7 text-gray-600">8692 Wild Rose Drive
                                    <br />Livonia, MI 48150</div>
                                </div>
                                <div className="col-sm-3">
                                    <div className="fw-bold fs-7 text-gray-600 mb-1">Issued By:</div>
                                    <div className="fw-bolder fs-6 text-gray-800">
                                        DSPro LTD
                                    </div>
                                    <div className="fw-bold fs-7 text-gray-600">
                                        9858 South 53rd Ave.
                                        <br />Matthews, NC 28104
                                    </div>
                                </div>
                            </div> */}

                            <div className="flex-grow-1">
                                <div className="table-responsive border-bottom mb-9">
                                    <table className="table mb-3">
                                        <thead>
                                            <tr className="border-bottom fs-6 fw-bolder text-muted">
                                                <th className="min-w-175px pb-2">Description</th>
                                                <th className="min-w-80px text-end pb-2">Rate</th>
                                                <th className="min-w-100px text-end pb-2">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="fw-bolder text-gray-700 fs-5 text-end">
                                                <td className="align-middle text-start">
                                                    <div>Monthly Subscription</div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="fs-7 fw-normal">+ 5 Online Seats</span>
                                                        <a href="#" className="ms-2"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                window.openModal("modal-help-subscription");
                                                            }}>
                                                            <Icon icon="circle-question" type="solid" size={14} />
                                                        </a>
                                                    </div>
                                                </td>
                                                <td className="pt-6">{moneyFormatter.format(prices?.subscriptionPerMonth ?? 0)}</td>
                                                <td className="pt-6 text-dark fw-boldest">{moneyFormatter.format(prices?.subscriptionPerMonth ?? 0)}</td>
                                            </tr>
                                            <tr className="fw-bolder text-gray-700 fs-5 text-end">
                                                <td className="align-middle text-start">
                                                    <FlexRow>
                                                        <div>Initial Budget</div>
                                                    </FlexRow>
                                                    <div className="d-flex align-items-center">
                                                        <span className="fs-7 fw-normal">
                                                            Define budget charge limit
                                                        </span>
                                                        <a href="#" className="ms-2"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                window.openModal("modal-help-initialbudget");
                                                            }}>
                                                            <Icon icon="circle-question" type="solid" size={14} />
                                                        </a>
                                                    </div>
                                                </td>
                                                <td className="align-middle text-end">
                                                    {/* <input id="txt-monthlyBudget" className="form-control form-control-solid form-control-sm ms-auto w-150px" maxLength={4}
                                                        type="tel" placeholder="50$ - 300$" name="monthlyBudget" autoComplete="one-time-code"
                                                        onChange={budgetChange} onKeyPress={budgetKeypress} /> */}
                                                    <select name="monthlybudget" className="form-select form-select-solid form-select-sm ms-auto w-150px" value={monthlyBudget}
                                                        onChange={budgetChange}>
                                                        {/* <option value={0}>Choose Budget</option> */}
                                                        <option value={25}>$25</option>
                                                        <option value={50}>$50</option>
                                                        <option value={100}>$100</option>
                                                        <option value={150}>$150</option>
                                                        <option value={200}>$200</option>
                                                        <option value={250}>$250</option>
                                                        <option value={300}>$300</option>
                                                    </select>
                                                    <div id="invoice-budget-err" className="small text-danger mx-2 mt-1 form-error"></div>
                                                </td>
                                                <td className="pt-6 text-dark fw-boldest">{moneyFormatter.format(monthlyBudget)}</td>
                                            </tr>
                                            <tr className="fw-bolder text-gray-700 fs-5 text-end">
                                                <td className="align-middle text-start">
                                                    <div>Coupon</div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="fs-7 fw-normal">
                                                            If you have a discount code
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="align-middle text-end">
                                                    <div className="ms-auto w-150px"
                                                        style={{
                                                            position: "relative"
                                                        }}>
                                                        <input type="text" className="form-control form-control-solid form-control-sm" maxLength={8} style={{ textTransform: "uppercase" }}
                                                            placeholder="Coupon Code" value={couponCode} />
                                                        {/* onChange={e => couponCodeSet(e.target.value)} */}
                                                        {
                                                            couponChecking &&
                                                            <span style={{
                                                                position: "absolute",
                                                                right: 10,
                                                                top: 6
                                                            }}>
                                                                <Icon icon="asterisk" spin color="#777" />
                                                            </span>
                                                        }
                                                        {
                                                            couponIsValid &&
                                                            <span style={{
                                                                position: "absolute",
                                                                right: 10,
                                                                top: 6
                                                            }}>
                                                                <Icon icon="circle-check" type="solid" className="text-success" />
                                                            </span>
                                                        }
                                                        {
                                                            couponIsInvalid &&
                                                            <span style={{
                                                                position: "absolute",
                                                                right: 10,
                                                                top: 6
                                                            }} title="Coupon code is invalid!">
                                                                <Icon icon="circle-exclamation" type="solid" className="text-warning" />
                                                            </span>
                                                        }
                                                    </div>
                                                </td>
                                                <td className="pt-6 text-dark fw-boldest">{moneyFormatter.format(couponAmount)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <FlexRow>

                                    <div>
                                        <FlexRow>
                                            <button type="button" onClick={payInvoice} className="btn btn-primary w-250px">
                                                {
                                                    working ?
                                                        <>
                                                            <span className="indicator-label me-2">
                                                                Please Wait
                                                            </span>
                                                            <Icon icon="spinner-third" spin />
                                                        </>
                                                        :
                                                        <>
                                                            <span className="indicator-label me-2">
                                                                Proceed to Checkout
                                                            </span>
                                                            <Icon icon="chevron-right" type="solid" />
                                                        </>
                                                }
                                            </button>
                                            <div id="invoice-err" className="small text-danger mx-2 form-error"></div>
                                        </FlexRow>
                                        <div className="mt-3 ms-1">
                                            <button type="button" onClick={e => updateStep(4)} className="btn btn-link w-250px text-gray-600">
                                                <Icon icon="chevron-left" className="me-2" />
                                                Back to Payment Methods
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mw-300px ms-auto">
                                        <div className="d-flex flex-stack mb-3">
                                            <div className="fw-bold pe-10 text-gray-600 fs-7">Subtotal:</div>
                                            <div className="text-end fw-bolder fs-6 text-gray-800">{moneyFormatter.format(subTotalAmount)}</div>
                                        </div>
                                        <div className="d-flex flex-stack mb-3">
                                            <div className="fw-bold pe-10 text-gray-600 fs-7">Discount</div>
                                            {
                                                couponAmount > 0 ?
                                                    <div className="text-end fw-bolder fs-6 text-danger">- {moneyFormatter.format(couponAmount)}</div>
                                                    :
                                                    <div className="text-end fw-bolder fs-6 text-gray-800">{moneyFormatter.format(0)}</div>
                                            }
                                        </div>
                                        <div className="d-flex flex-stack mb-3">
                                            <div className="fw-bold pe-10 text-gray-600 fs-7">VAT 0%</div>
                                            <div className="text-end fw-bolder fs-6 text-gray-800">{moneyFormatter.format(0)}</div>
                                        </div>
                                        <div className="d-flex flex-stack">
                                            <div className="fw-bold pe-10 text-gray-600 fs-4">Total</div>
                                            <div className="text-end fw-bolder fs-1 text-gray-800">{moneyFormatter.format(totalAmount)}</div>
                                        </div>
                                        <FlexRow className="mt-5">
                                            <div className="me-2 fs-8">
                                                Powered by
                                            </div>
                                            <Icon icon="stripe" type="brands" className="text-info" size={24} />
                                        </FlexRow>
                                    </div>

                                </FlexRow>

                            </div>

                        </div>
                    </div>
                </form>
            </Container>

            <Container visible={step == 6}>
                {/* The one step whose action is not in the bottom bar. It is a moment rather than
                    a form — there is nothing to fill in and nothing to skip — so the button
                    belongs under the words it follows from. */}
                <Form onSubmit={() => beginsetup(req?.token, req?.userId)} className="form w-100 signup-welcome">

                    <div className="signup-welcome-mark">
                        <Icon icon="check" type="solid" size={34} />
                    </div>

                    <h1 className="signup-welcome-title">You{String.fromCharCode(39)}re all set</h1>

                    <div className="signup-welcome-lead">
                        Your account is ready{planLabel ? " on the " + planLabel + " plan" : ""}.
                    </div>

                    <div className="signup-welcome-next">
                        Next we{String.fromCharCode(39)}ll set up the basics — the work you do, where calls ring and the number
                        customers call. None of it is required now, and any of it can be done from the
                        console later.
                    </div>

                    <button type="submit" className="btn btn-lg btn-primary signup-welcome-button" disabled={working}>
                        {
                            working ?
                                <Icon icon="asterisk" spin />
                                :
                                <>
                                    <span className="indicator-label me-2">Let{String.fromCharCode(39)}s Begin</span>
                                    <Icon icon="chevron-right" />
                                </>
                        }
                    </button>

                    {
                        // Only a free account can go back and pick something else. A paid plan
                        // has already been charged, so there is nothing to return to.
                        //
                        // Down in the bar rather than under the button: this step's own action is
                        // the one in the middle of the page, and going back is a footnote to it.
                        finishedOnFreePlan &&
                        <SignupActions>
                            <button type="button" className="btn btn-light"
                                onClick={e => updateStep(4)}>
                                <Icon icon="chevron-left" className="me-2" />
                                Change your plan
                            </button>
                        </SignupActions>
                    }
                </Form>
            </Container>

            <Container visible={step == 7}>
                <div className="w-100">
                    <div className="mb-10 text-center">
                        <div className="text-center mb-10">
                            <Icon type="thin" icon="briefcase" size={96} className="me-2 text-primary" />
                        </div>
                        <h1 className="fw-bolder fs-2qx text-gray-800 mb-7">
                            What Kind of Work Do You Do?
                        </h1>
                        <div className="text-gray-600 fs-6">
                            Job types are what every job, price and report is built on.
                        </div>
                        <div className="text-gray-600 fs-6">
                            Group them the way your business thinks about them — you can change all of this later.
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>

                    <SignupJobTypes
                        onDone={() => updateStep(8)}
                        onSkip={skipStep}
                        onSkipSetup={skipSetup} />
                </div>
            </Container>

            <Container visible={step == 8}>
                <div className="w-100">
                    <div className="mb-10 text-center">
                        <div className="text-center mb-10">
                            <Icon type="thin" icon="bullhorn" size={96} className="me-2 text-primary" />
                        </div>
                        <h1 className="fw-bolder fs-2qx text-gray-800 mb-7">
                            Where Does Your Work Come From?
                        </h1>
                        <div className="text-gray-600 fs-6">
                            Sources are how every job is credited — which company sent it, or which advert it came from.
                        </div>
                        <div className="text-gray-600 fs-6">
                            Add as many as you like now, and the rest whenever you need them.
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>

                    <SignupJobSources
                        onDone={() => updateStep(9)}
                        onSkip={skipStep}
                        onSkipSetup={skipSetup} />
                </div>
            </Container>

            {/* The money the account runs on, and the gate in front of the two number steps.
                There is no "skip to the next step" here on purpose: skipping the wallet skips
                the number too, because a number cannot be given to an account with nothing in
                it. The only two ways off this step are paying and leaving for the console. */}
            <Container visible={step == 9}>
                <div className="w-100">
                    <div className="mb-10 text-center">
                        <div className="text-center mb-10">
                            <Icon type="thin" icon="wallet" size={96} className="me-2 text-primary" />
                        </div>
                        <h1 className="fw-bolder fs-2qx text-gray-800 mb-7">
                            Add Credit to Your Wallet
                        </h1>
                        <div className="text-gray-600 fs-6">
                            Your wallet pays for your number, your calls and your messages — top up any time from the console.
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-10">
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                        <div className="border-bottom border-gray-300 mw-50 w-100"></div>
                    </div>

                    {
                        step == 9 &&
                        <SignupWallet
                            token={req?.token}
                            onDone={() => updateStep(10)}
                            onSkipSetup={skipSetup} />
                    }
                </div>
            </Container>

            <Container visible={step == 11}>
                {
                    step == 11 && selectedLine &&
                    <SignupNumberSettings
                        token={req?.token}
                        line={selectedLine}
                        numberType={selectedNumberType?.id}
                        onBack={() => stepSet(10)}
                        onSkipSetup={skipSetup}
                        onDone={() => stepSet(12)} />
                }
            </Container>

            {/* Set up. The way into the console is the one thing on the page. */}
            <Container visible={step == 12}>
                <div className="w-100 text-center py-10">
                    <Icon type="thin" icon="check-circle" size={96} className="text-success mb-8" />
                    <h1 className="fw-bolder fs-2qx text-gray-800 mb-5">You're all set!</h1>
                    <div className="text-gray-600 fs-5 mb-10">
                        Your account and your phone number are ready.
                    </div>
                    <button type="button" className="btn btn-lg btn-primary min-w-250px" disabled={working}
                        onClick={() => { workingSet(true); finalize(); }}>
                        {
                            working ?
                                <>
                                    <span className="indicator-label me-2">Opening</span>
                                    <Icon icon="spinner-third" spin />
                                </>
                                :
                                <>
                                    <span className="indicator-label me-2">Go to Console</span>
                                    <Icon icon="arrow-right" type="solid" />
                                </>
                        }
                    </button>
                </div>
            </Container>
            <Container visible={step == 10}>
                <div className="form w-100">

                    <div className="row">
                        <div className="col-12">

                            <div className="row border-bottom pb-2 mb-2">
                                <div className="col-12">
                                    <Form onSubmit={(body, callback) => {
                                        fetchLines(body);
                                    }}>
                                        <FlexRow className="flex-wrap gap-3">
                                            {
                                                /* No country to choose. Only US numbers can be
                                                   sold today, so the country is fixed and posted
                                                   with the search rather than asked for. */
                                            }
                                            <input type="hidden" name="country" value={getUSA().id} />

                                            <div className="w-250px">
                                                <Select id="drp-areacodes" name="areaCode" block marginBottom={0}
                                                    placeholder="Area code or state"
                                                    searchPlaceholder="Search an area code or a state..."
                                                    source={usAreaCodeItems(states)}
                                                    dataGroup="group"
                                                    searchable />
                                            </div>
                                            <div className="w-200px">
                                                <TextBox id="txt-lines-keyword" name="keyword" block marginBottom={0}
                                                    numeric placeholder="Part of the number" maxLength={8} />
                                            </div>
                                            {/* Every press brings a fresh list of numbers. */}
                                            <button className="btn btn-primary w-125px ms-auto">
                                                <Icon icon="arrows-rotate" className="me-2" />
                                                Refresh
                                            </button>
                                        </FlexRow>
                                    </Form>
                                </div>
                            </div>
                            {
                                fetchingLines &&
                                <div className="row h-400px h-xxl-600px">
                                    <div className="col-12 p-10">
                                        <Indicator />
                                    </div>
                                </div>
                            }
                            {
                                fetchedLines &&
                                // Up to 50 numbers, five to a row. No height of its own and no
                                // scroll of its own: the content column it sits in is the one box
                                // on the page that scrolls, and a second scroller nested inside it
                                // only ever means two scrollbars arguing over one wheel.
                                <div className="pt-2">
                                    {
                                        /* What it costs, before one is picked rather than after.
                                           This used to show nothing, on the grounds that every
                                           plan sold here included the first number -- Free no
                                           longer does, and a charge nobody mentioned turning up
                                           on the wallet is not the way to tell them. */
                                        numberQuote &&
                                        <div className="signup-number-price">
                                            <Icon icon={numberQuote.isFree ? "circle-check" : "wallet"} type="solid" size={14} className="me-2" />
                                            <span className="fw-bolder me-2">{numberQuote.priceText}</span>
                                            {
                                                numberQuote.note &&
                                                <span className="text-gray-500">{numberQuote.note}</span>
                                            }
                                        </div>
                                    }
                                    <NumberResults
                                        lines={lines}
                                        selected={selectedLine}
                                        onSelect={selectedLineSet} />
                                </div>
                            }

                        </div>
                    </div>

                    <SignupActions>
                        <button type="button" className="btn btn-light" onClick={skipSetup} disabled={working}>
                            Skip setup &amp; go to console
                        </button>
                        <button type="button" className="btn btn-lg btn-primary min-w-200px ms-auto" disabled={working} onClick={chooseNumber}>
                            {
                                working ?
                                    <>
                                        <span className="indicator-label me-2">
                                            Please Wait
                                        </span>
                                        <Icon icon="spinner-third" spin />
                                    </>
                                    :
                                    <>
                                        <span className="indicator-label me-2">
                                            Continue
                                        </span>
                                        <Icon icon="chevron-right" type="solid" />
                                    </>
                            }
                        </button>
                    </SignupActions>

                </div>
            </Container>

            <Modal id="modal-remove-method"
                title="Remove Payment Method"
                buttonText="Remove Card"
                buttonColor="danger"
                onConfirm={callback => {
                    hub.Post("/auth/signup/removepaymentmethod", {
                        id: selectedItem?.id,
                        userId: req?.userId,
                        token: req?.token
                    }, res => {
                        if (res.success) fetchPaymentMethods(callback);
                    })
                }}>
                <div className="py-10">
                    {
                        selectedItem && <PaymentCardView item={selectedItem} />
                    }
                </div>
            </Modal>


            <Modal id="modal-help-subscription"
                title="Monthly Subscription"
                titleIcon="circle-question">
                <div className="mb-10 mt-5">
                    <p>Your subscription plan includes 5 subaccounts, allowing you to add Admin and Agent users to manage your team. </p>
                    <p>If you need more than 5 subaccounts, you can easily expand your team by purchasing additional seats through our Extra Seats package.</p>
                </div>
            </Modal>

            <Modal id="modal-help-initialbudget"
                title="Monthly Subscription"
                titleIcon="circle-question">
                <div className="mb-10 mt-5">
                    <p>The initial budget in our CRM functions as a pre-set funding amount for your wallet, which is used to cover expenses such as purchasing phone numbers, call usage, and SMS charges. </p>
                    <p>When you set an initial budget—say, $50—this amount is added to your wallet to start. If your wallet balance reaches zero, the system automatically recharges your wallet with another $50, ensuring continuous service without interruption. </p>
                    <p>This automatic recharge occurs based on the initial budget amount you choose, streamlining your payments and account management.</p>
                </div>
            </Modal>

            <Modal id="modal-help-whatisextension"
                title="What is Extension?"
                titleIcon="circle-question">
                <div className="mb-10 mt-5">
                    <p>
                        If you’ve ever been to an office looking for someone and the receptionist dialed a short number and then directed you to the person, you probably have an idea of a phone extension. A VoIP phone extension is a short number – usually three, four, or five digits – that a caller can dial to reach an organization’s specific department or employee.
                    </p>
                    <p>
                        You set up and assign phone extension numbers to represent different segments of your team, such as internal lines in conference rooms and departments. As with traditional lines, phone extensions are linked to your business phone numbers and function in the same way. The only difference is that, unlike phone lines, you can use extensions to cover many departments and employees without investing in extra lines.
                    </p>
                    <p>
                        They are also efficient and convenient. If a client needs to track their package, they can dial the short number and immediately get connected to your shipping team. Similarly, users within the same organization can reach one another by dialing the three-digit extensions instead of hunting for the entire 10-digit phone number.
                    </p>
                </div>
            </Modal>

            <Modal id="modal-help-whatisqueue"
                title="What is Queue?"
                titleIcon="circle-question">
                <div className="mb-10 mt-5">
                    <p>
                        A queue is a powerful feature used to manage a high volume of incoming calls by placing callers in a line to wait for the next available representative. This system is particularly useful in customer service settings where multiple callers might be trying to reach support at the same time. Each caller is automatically added to the queue and held there until someone is free to answer their call.
                    </p>
                    <p>
                        Queues allow you to prioritize calls based on various criteria, such as how long a caller has been waiting or the importance of their inquiry. You can also configure announcements that play to the caller while they wait, providing information like estimated wait time or offering alternative contact options such as email or a website visit. This keeps the caller informed and can improve their overall experience despite the wait.
                    </p>
                    <p>
                        Additionally, queues are customizable with features like call-back options, where callers can choose to hang up and receive a call back when it’s their turn, rather than staying on the line. Managers can monitor queue status, track performance metrics like average wait time and call abandonment rates, and adjust settings to optimize call flow and agent efficiency. This level of management helps ensure that resources are used effectively and callers receive the best service possible.
                    </p>
                </div>
            </Modal>

        </div>
    )
}

export default SignUpComplete;